const REQUEST_ID_HEADER = 'x-request-id';
const TRACEPARENT_HEADER = 'traceparent';

const TRACEPARENT_PATTERN = /^([0-9a-f]{2})-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/i;

const REQUEST_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DEFAULT_TRACE_FLAGS = '01';

interface HeaderBag {
  [key: string]: string | string[] | undefined;
}

export interface ParsedTraceParent {
  version: string;
  traceId: string;
  parentId: string;
  traceFlags: string;
  raw: string;
}

export interface CorrelationContext {
  requestId: string;
  traceContext: ParsedTraceParent;
  requestIdSource: 'header' | 'generated';
  traceIdSource: 'header' | 'generated';
}

export { REQUEST_ID_HEADER, TRACEPARENT_HEADER };

export const normalizeHeaderName = (name: string): string => name.toLowerCase().trim();

export const normalizeHeaderValue = (value: string | string[] | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = (Array.isArray(value) ? value[0] : value).trim();
  return normalized.length > 0 ? normalized : undefined;
};

const toHex = (length: number): string => {
  const bytes = new Uint8Array(Math.ceil(length / 2));
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, length);
  }

  // fallback for environments without crypto.getRandomValues
  const chars = '0123456789abcdef';
  let output = '';
  for (let i = 0; i < length; i += 1) {
    output += chars[Math.floor(Math.random() * chars.length)];
  }
  return output;
};

export const generateRequestId = (): string => {
  const crypto = globalThis.crypto;
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // fallback keeps compatibility if randomUUID is unavailable
  return `${toHex(8)}-${toHex(4)}-${toHex(4)}-${toHex(4)}-${toHex(12)}`;
};

export const generateTraceId = (): string => toHex(32);

export const generateParentId = (): string => toHex(16);

export const parseRequestId = (value?: string): string | undefined => {
  const normalized = normalizeHeaderValue(value);
  if (!normalized) {
    return undefined;
  }

  return REQUEST_ID_PATTERN.test(normalized) ? normalized : undefined;
};

export const parseTraceParent = (value?: string): ParsedTraceParent | undefined => {
  const normalized = normalizeHeaderValue(value)?.toLowerCase();
  if (!normalized) {
    return undefined;
  }

  const match = TRACEPARENT_PATTERN.exec(normalized);
  if (!match) {
    return undefined;
  }

  const [, version, traceId, parentId, traceFlags] = match;
  return { version, traceId, parentId, traceFlags, raw: normalized };
};

export const buildTraceParent = (
  traceId: string,
  parentId: string = generateParentId(),
  traceFlags = DEFAULT_TRACE_FLAGS,
  version = '00',
): string => `${version}-${traceId}-${parentId}-${traceFlags}`;

export const resolveCorrelationContext = (headers: HeaderBag): CorrelationContext => {
  const requestIdHeader = normalizeHeaderValue(headers[REQUEST_ID_HEADER]);
  const parsedRequestId = parseRequestId(requestIdHeader);

  const requestId = parsedRequestId ?? generateRequestId();
  const requestIdSource = parsedRequestId ? 'header' : 'generated';

  const traceparentHeader = normalizeHeaderValue(headers[TRACEPARENT_HEADER]);
  const parsedTraceParent = parseTraceParent(traceparentHeader);

  const traceId = parsedTraceParent?.traceId ?? generateTraceId();
  const traceIdSource = parsedTraceParent ? ('header' as const) : ('generated' as const);
  const traceParentRaw = parsedTraceParent
    ? parsedTraceParent.raw
    : buildTraceParent(traceId, generateParentId());

  const traceContext: ParsedTraceParent = {
    version: parsedTraceParent?.version ?? '00',
    traceId,
    parentId: parsedTraceParent?.parentId ?? generateParentId(),
    traceFlags: parsedTraceParent?.traceFlags ?? DEFAULT_TRACE_FLAGS,
    raw: traceParentRaw,
  };

  return {
    requestId,
    traceContext,
    requestIdSource,
    traceIdSource,
  };
};
